import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import type { ReactNode } from "react";

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  marginTop: theme.spacing(1),
}));

const StyledDialogContentText = styled(DialogContentText)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(3),
  paddingBottom: theme.spacing(2),
}));

interface ConfirmationDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Dialog title */
  title: string;
  /** Confirmation message (plain string or rich content) */
  message: ReactNode;
  /** Confirm button text */
  confirmText: string;
  /** Confirm button color (default: "primary") */
  confirmColor?: "error" | "primary" | "inherit";
  /** Confirm button variant (default: "contained") */
  confirmVariant?: "contained" | "text" | "outlined";
  /** Cancel button text (default: "Cancel") */
  cancelText?: string;
  /** Callback when confirmed */
  onConfirm: () => void;
  /** Callback when cancelled */
  onCancel: () => void;
  /** Whether the confirm action is loading */
  isLoading?: boolean;
  /** Dialog max width (default: "xs") */
  maxWidth?: "xs" | "sm" | "md" | "lg" | "xl";
  /** Optional input field label */
  inputLabel?: string;
  /** Optional input field value */
  inputValue?: string;
  /** Optional input change handler */
  onInputChange?: (value: string) => void;
  /** Number of rows for multiline input (default: 3) */
  inputRows?: number;
}

export function ConfirmationDialog({
  open,
  title,
  message,
  confirmText,
  confirmColor = "primary",
  confirmVariant = "contained",
  cancelText = "Cancel",
  onConfirm,
  onCancel,
  isLoading = false,
  maxWidth = "xs",
  inputLabel,
  inputValue,
  onInputChange,
  inputRows = 3,
}: ConfirmationDialogProps) {
  const hasInput = inputLabel && onInputChange !== undefined;

  return (
    <Dialog open={open} onClose={onCancel} maxWidth={maxWidth} fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <StyledDialogContent>
        <StyledDialogContentText>{message}</StyledDialogContentText>
        {hasInput && (
          <TextField
            label={inputLabel}
            multiline
            rows={inputRows}
            fullWidth
            value={inputValue}
            onChange={(e) => onInputChange(e.target.value)}
          />
        )}
      </StyledDialogContent>
      <StyledDialogActions>
        <Button onClick={onCancel}>{cancelText}</Button>
        <Button
          onClick={onConfirm}
          color={confirmColor}
          variant={confirmVariant}
          disabled={isLoading}
        >
          {confirmText}
        </Button>
      </StyledDialogActions>
    </Dialog>
  );
}
